import { Alert, Box, Button, Container, Grid, Paper, TextField, Toolbar, Typography } from '@mui/material';
import { getAuth } from 'firebase/auth';
import * as React from 'react';
import InputFileUpload from '../../components/events/InputFileUpload';
import { createClub } from '../../services/clubs';
import { getGeoCodeFromAdress, uploadToStorage } from '../../util/firebaseUtil';

type ClubFormData = {
    city: string;
    name: string;
    plz: string;
    street: string;
    website: string;
};

const initialFormData: ClubFormData = {
    city: '',
    name: '',
    plz: '',
    street: '',
    website: '',
};

function buildImageFileName(file: File, clubName: string) {
    const extension = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '.jpg';
    const safeName = clubName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'club';
    return `club-images/${safeName}-${Date.now()}${extension}`;
}

export default function CreateClubPage() {
    const [formData, setFormData] = React.useState<ClubFormData>(initialFormData);
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');

    const canSubmit = Boolean(
        formData.name.trim()
        && formData.website.trim()
        && formData.street.trim()
        && formData.plz.trim()
        && formData.city.trim()
        && selectedFile
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((current) => ({ ...current, [name]: value }));
    };

    const resetForm = () => {
        setFormData(initialFormData);
        setSelectedFile(null);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSuccessMessage('');
        setErrorMessage('');

        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setErrorMessage('Not authenticated.');
            return;
        }
        if (!selectedFile) {
            setErrorMessage('Club image is required.');
            return;
        }

        try {
            setIsSubmitting(true);

            const address = `${formData.street}, ${formData.plz} ${formData.city}`;
            const { lat, lng } = await getGeoCodeFromAdress(address);
            if (lat === undefined || lng === undefined) {
                setErrorMessage('Address could not be geocoded.');
                return;
            }

            const imageUrl = await uploadToStorage(
                selectedFile,
                buildImageFileName(selectedFile, formData.name),
            );
            if (!imageUrl) {
                setErrorMessage('Club image upload failed.');
                return;
            }

            const token = await currentUser.getIdToken();
            const response = await createClub({
                name: formData.name.trim(),
                contact: {
                    WEBSITE: formData.website.trim(),
                },
                address: {
                    city: formData.city.trim(),
                    plz: formData.plz.trim(),
                    street: formData.street.trim(),
                },
                clubImage: imageUrl,
                location: {
                    latitude: lat,
                    longitude: lng,
                },
            }, token);

            const createdClubId = response?.clubId ? ` (${response.clubId})` : '';
            setSuccessMessage(`Club created successfully${createdClubId}.`);
            resetForm();
        } catch (error: any) {
            setErrorMessage(error?.message ?? 'Failed to create club.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            component="main"
            sx={{
                backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                        ? theme.palette.grey[100]
                        : theme.palette.grey[900],
                flexGrow: 1,
                height: '100vh',
                overflow: 'auto',
            }}
        >
            <Toolbar />
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Add Club
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                        Create a new club with the same data shape currently used by `club_pusher.py`.
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <InputFileUpload selectedFile={selectedFile} setSelectedFile={setSelectedFile} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Club name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Website / Instagram URL"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Street"
                                    name="street"
                                    value={formData.street}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Postal code"
                                    name="plz"
                                    value={formData.plz}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>

                        {successMessage && (
                            <Alert severity="success" sx={{ mt: 3 }}>
                                {successMessage}
                            </Alert>
                        )}
                        {errorMessage && (
                            <Alert severity="error" sx={{ mt: 3 }}>
                                {errorMessage}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!canSubmit || isSubmitting}
                            sx={{ mt: 3 }}
                        >
                            {isSubmitting ? 'Creating...' : 'Create club'}
                        </Button>
                    </form>
                </Paper>
            </Container>
        </Box>
    );
}
